import { ChangeEvent, KeyboardEvent, MouseEvent, ReactNode, useEffect, useState } from "react";
import { ACCEPTED_MIME, FOLDERS, MAX_FILES, RAW_FILES, TYPE_CONFIG } from "../constants";
import { FileType, ManagedFile, PreviewModal, QueuedFile, RenameModal, UploadStatus } from "../types";
import { IconBtn } from "../../../components/common/UI/Button/IconBtn";

// ─── All Files ─────────────────────────────────────────────────────────

export function generateFiles(): ManagedFile[] {
    return RAW_FILES.map((f, i) => ({ ...f, id: `file-${String(i + 1).padStart(3, "0")}` }));
}

export function fmtDate(y: number, m: number, d: number) {
    return new Date(y, m, d).toISOString();
}

export function fmtSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
    return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

export function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function highlight(text: string, q: string): ReactNode {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark style={{ background: "rgba(191,61,12,0.3)", color: "var(--or)", borderRadius: 2, padding: "0 1px" }}>
                {text.slice(idx, idx + q.length)}
            </mark>
            {text.slice(idx + q.length)}
        </>
    );
}

export function getExt(name: string): string {
    return name.split(".").pop()?.toUpperCase() ?? "FILE";
}

export function FileThumbnail({ file, size = 44 }: { file: ManagedFile; size?: number }) {
    const cfg = TYPE_CONFIG[file.type];
    const ext = getExt(file.name);
    return (
        <div style={{
            width: size, height: size, borderRadius: 8, flexShrink: 0,
            background: cfg.bg, border: `1px solid ${cfg.border}`,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 2,
        }}>
            <span style={{ fontSize: size * 0.38, lineHeight: 1 }}>{cfg.icon}</span>
            {size >= 40 && (
                <span style={{ fontSize: 8, fontWeight: 700, color: cfg.color, letterSpacing: "0.5px" }}>{ext}</span>
            )}
        </div>
    );
}

export function TypeBadge({ type }: { type: FileType }) {
    const cfg = TYPE_CONFIG[type];
    return (
        <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: "0.4px",
            background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color,
            borderRadius: 4, padding: "2px 7px", whiteSpace: "nowrap",
        }}>{cfg.label}</span>
    );
}

export function RenameModalComp({ modal, onConfirm, onCancel }: {
    modal: RenameModal;
    onConfirm: (newName: string) => void;
    onCancel: () => void;
}) {
    const [value, setValue] = useState(modal.name);
    useEffect(() => { setValue(modal.name); }, [modal.name]);
    if (!modal.open) return null;
    const handleKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") onConfirm(value); };
    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={onCancel}>
            <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{
                background: "var(--card)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16,
                padding: 28, width: 420, maxWidth: "90vw", animation: "modalIn 0.2s ease",
            }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 16 }}>Rename file</div>
                <input
                    autoFocus value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={handleKey}
                    style={{
                        width: "100%", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)",
                        borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "var(--text)",
                        outline: "none", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box",
                        marginBottom: 20,
                    }}
                />
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={onCancel} style={{
                        padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)",
                        background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                    }}>Cancel</button>
                    <button onClick={() => onConfirm(value)} style={{
                        padding: "9px 20px", borderRadius: 8, border: "1px solid rgba(191,61,12,0.35)",
                        background: "var(--or-dim)", color: "var(--or)", fontSize: 14,
                        fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
                    }}>Rename</button>
                </div>
            </div>
        </div>
    );
}

// ─── Preview modal ────────────────────────────────────────────────────────────

export function PreviewModalComp({ modal, onClose }: { modal: PreviewModal; onClose: () => void }) {
    if (!modal.open || !modal.file) return null;
    const f = modal.file;
    const cfg = TYPE_CONFIG[f.type];
    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }} onClick={onClose}>
            <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{
                background: "#1a1917", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16,
                width: 600, maxWidth: "92vw", maxHeight: "90vh", overflow: "hidden",
                display: "flex", flexDirection: "column", animation: "modalIn 0.2s ease",
            }}>
                {/* Header */}
                <div style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "16px 20px",
                    borderBottom: "1px solid rgba(255,255,255,0.07)",
                }}>
                    <FileThumbnail file={f} size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{fmtSize(f.size)} · {f.folder}</div>
                    </div>
                    <button onClick={onClose} style={{
                        width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
                        background: "transparent", color: "var(--text3)", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
                    }}>×</button>
                </div>

                {/* Preview area */}
                <div style={{
                    flex: 1, overflowY: "auto", display: "flex", flexDirection: "column",
                }}>
                    <div style={{
                        minHeight: 200, background: "rgba(0,0,0,0.3)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        borderBottom: "1px solid rgba(255,255,255,0.07)", padding: 24,
                    }}>
                        {f.type === "image" && f.url ? (
                            <img src={f.url} alt={f.name} style={{ maxHeight: 340, maxWidth: "100%", objectFit: "contain", borderRadius: 8 }} />
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <div style={{
                                    width: 72, height: 72, borderRadius: 16, background: cfg.bg, border: `1px solid ${cfg.border}`,
                                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, margin: "0 auto 12px",
                                }}>{cfg.icon}</div>
                                <div style={{ fontSize: 24, fontWeight: 700, color: cfg.color, marginBottom: 4 }}>.{getExt(f.name)}</div>
                                <div style={{ fontSize: 13, color: "var(--text3)" }}>Preview not available for this file type</div>
                            </div>
                        )}
                    </div>

                    {/* Meta grid */}
                    <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {[
                            { label: "File name", value: f.name },
                            { label: "File type", value: cfg.label },
                            { label: "Size", value: fmtSize(f.size) },
                            { label: "Folder", value: f.folder },
                            { label: "Uploaded by", value: f.uploadedBy },
                            { label: "Uploaded on", value: formatDate(f.createdAt) },
                            ...(f.dimensions ? [{ label: "Dimensions", value: f.dimensions }] : []),
                            ...(f.duration ? [{ label: "Duration", value: f.duration }] : []),
                        ].map((row) => (
                            <div key={row.label} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "10px 14px" }}>
                                <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 4 }}>{row.label}</div>
                                <div style={{ fontSize: 13, color: "var(--text2)", wordBreak: "break-all" }}>{row.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Grid card ────────────────────────────────────────────────────────────────

export function FileCard({
    file, selected, searchQuery, onSelect, onPreview, onRename, onCopyUrl, onDelete,
}: {
    file: ManagedFile; selected: boolean; searchQuery: string;
    onSelect: () => void; onPreview: () => void; onRename: () => void;
    onCopyUrl: () => void; onDelete: () => void;
}) {
    const [hov, setHov] = useState(false);
    const cfg = TYPE_CONFIG[file.type];
    return (
        <div
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{
                background: selected ? "rgba(191,61,12,0.06)" : "var(--card)",
                border: `1px solid ${selected ? "rgba(191,61,12,0.28)" : hov ? "rgba(255,255,255,0.12)" : "var(--border)"}`,
                borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column",
                transition: "all 0.15s", cursor: "default",
                boxShadow: hov ? "0 4px 20px rgba(0,0,0,0.28)" : "none",
            }}
        >
            {/* Thumbnail area */}
            <div
                onClick={onPreview}
                style={{
                    height: 110, background: "rgba(255,255,255,0.02)", position: "relative",
                    borderBottom: "1px solid var(--border)",
                    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                }}
            >
                <div style={{
                    width: 52, height: 52, borderRadius: 10, background: cfg.bg, border: `1px solid ${cfg.border}`,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                    transition: "transform 0.15s",
                    transform: hov ? "scale(1.06)" : "scale(1)",
                }}>
                    <span style={{ fontSize: 24, lineHeight: 1 }}>{cfg.icon}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: cfg.color, letterSpacing: "0.5px" }}>{getExt(file.name)}</span>
                </div>

                {/* Checkbox */}
                <div style={{ position: "absolute", top: 8, left: 8 }}>
                    <input type="checkbox" checked={selected} onChange={onSelect}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: "var(--or)", cursor: "pointer", width: 13, height: 13 }} />
                </div>
            </div>

            {/* Info */}
            <div style={{ padding: "10px 12px 8px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{
                    fontSize: 13, fontWeight: 500, color: "var(--text)", lineHeight: 1.3,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }} title={file.name}>
                    {highlight(file.name, searchQuery)}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: "var(--text3)" }}>{fmtSize(file.size)}</span>
                    <TypeBadge type={file.type} />
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)" }}>{file.folder} · {formatDate(file.createdAt)}</div>
            </div>

            {/* Actions */}
            <div style={{
                display: "flex", gap: 4, padding: "8px 10px",
                borderTop: "1px solid var(--border)", background: "rgba(255,255,255,0.01)",
            }}>
                <IconBtn title="Preview" onClick={onPreview}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                    </svg>
                </IconBtn>
                <IconBtn title="Copy URL" onClick={onCopyUrl} accent>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                </IconBtn>
                <IconBtn title="Rename" onClick={onRename}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                </IconBtn>
                <div style={{ flex: 1 }} />
                <IconBtn title="Delete" danger onClick={onDelete}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
                    </svg>
                </IconBtn>
            </div>
        </div>
    );
}

// ─── Upload New ──────────────────────────────────────────────────────────────

export function uid(): string { return Math.random().toString(36).slice(2, 10); }

export function detectType(file: File): FileType {
    return ACCEPTED_MIME[file.type] ?? "other";
}


export function StatusChip({ status, progress }: { status: UploadStatus; progress: number }) {
  const map: Record<UploadStatus, { label: string; color: string; bg: string; border: string }> = {
    queued: { label: "Queued", color: "var(--text3)", bg: "rgba(255,255,255,0.05)", border: "var(--border)" },
    uploading: { label: `${progress}%`, color: "#f5a623", bg: "rgba(245,166,35,0.10)", border: "rgba(245,166,35,0.25)" },
    done: { label: "Done", color: "#6ddc6d", bg: "rgba(109,220,109,0.10)", border: "rgba(109,220,109,0.25)" },
    error: { label: "Error", color: "#e05252", bg: "rgba(224,82,82,0.10)", border: "rgba(224,82,82,0.25)" },
  };
  const s = map[status];
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: "0.4px",
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      borderRadius: 20, padding: "3px 9px", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      {status === "uploading" && (
        <span style={{
          width: 6, height: 6, borderRadius: "50%", background: "#f5a623",
          animation: "pulse 1s ease-in-out infinite",
        }} />
      )}
      {s.label}
    </span>
  );
}

export function ProgressBar({ progress, status }: { progress: number; status: UploadStatus }) {
  const color = status === "done" ? "#6ddc6d" : status === "error" ? "#e05252" : "#f5a623";
  return (
    <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden", marginTop: 6 }}>
      <div style={{
        height: "100%", width: `${progress}%`, background: color,
        borderRadius: 2, transition: "width 0.3s ease, background 0.3s",
      }} />
    </div>
  );
}

function UploadFileThumbnail({ file, size = 44 }: { file: QueuedFile; size?: number }) {
  const cfg = TYPE_CONFIG[file.type];
  if (file.type === "image" && file.previewUrl) {
    return (
      <div style={{
        width: size, height: size, borderRadius: 8, flexShrink: 0,
        overflow: "hidden", border: `1px solid ${cfg.border}`, background: cfg.bg,
      }}>
        <img src={file.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
    }}>
      <span style={{ fontSize: size * 0.38, lineHeight: 1 }}>{cfg.icon}</span>
      {size >= 40 && <span style={{ fontSize: 8, fontWeight: 700, color: cfg.color, letterSpacing: "0.5px" }}>{getExt(file.file.name)}</span>}
    </div>
  );
}

// ─── Queue item ───────────────────────────────────────────────────────────────

export function QueueItem({
  item, expanded, onToggleExpand, onRemove, onFolderChange, onAltChange, onNameChange,
}: {
  item: QueuedFile;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  onFolderChange: (f: string) => void;
  onAltChange: (v: string) => void;
  onNameChange: (v: string) => void;
}) {
  const [hov, setHov] = useState(false);
  const isImage = item.type === "image";

  const inputStyle: React.CSSProperties = {
    width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
    borderRadius: 7, padding: "7px 10px", fontSize: 13, color: "var(--text)",
    outline: "none", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box",
    transition: "border-color 0.15s",
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: item.status === "done" ? "rgba(109,220,109,0.04)"
          : item.status === "error" ? "rgba(224,82,82,0.04)"
            : hov ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.015)",
        border: `1px solid ${item.status === "done" ? "rgba(109,220,109,0.15)"
            : item.status === "error" ? "rgba(224,82,82,0.15)"
              : hov ? "rgba(255,255,255,0.10)" : "var(--border)"
          }`,
        borderRadius: 12, overflow: "hidden",
        transition: "all 0.15s",
      }}
    >
      {/* Row header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
        <UploadFileThumbnail file={item} size={44} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
            {item.overrideName || item.file.name}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <TypeBadge type={item.type} />
            <span style={{ fontSize: 12, color: "var(--text3)" }}>{fmtSize(item.file.size)}</span>
            <span style={{ fontSize: 12, color: "var(--text3)" }}>{item.folder}</span>
          </div>
          {(item.status === "uploading" || item.status === "done") && (
            <ProgressBar progress={item.progress} status={item.status} />
          )}
          {item.status === "error" && item.error && (
            <div style={{ fontSize: 12, color: "#e05252", marginTop: 4 }}>{item.error}</div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <StatusChip status={item.status} progress={item.progress} />

          {/* Expand toggle */}
          {item.status !== "uploading" && item.status !== "done" && (
            <button
              onClick={onToggleExpand}
              title={expanded ? "Collapse" : "Edit metadata"}
              style={{
                width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
                background: expanded ? "var(--or-dim)" : "transparent",
                color: expanded ? "var(--or)" : "var(--text3)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, transition: "all 0.15s",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}

          {/* Remove */}
          {item.status !== "uploading" && (
            <button
              onClick={onRemove} title="Remove from queue"
              style={{
                width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
                background: "transparent", color: "var(--text3)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(224,82,82,0.10)"; e.currentTarget.style.color = "#e05252"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text3)"; }}
            >×</button>
          )}
        </div>
      </div>

      {/* Expandable metadata */}
      {expanded && item.status !== "done" && (
        <div style={{
          padding: "12px 14px 14px", borderTop: "1px solid var(--border)",
          background: "rgba(255,255,255,0.01)",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
        }}>
          {/* Override name */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", display: "block", marginBottom: 5 }}>
              Override filename
            </label>
            <input
              value={item.overrideName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
              placeholder={item.file.name}
              style={inputStyle}
              onFocus={(e) => { e.currentTarget.style.borderColor = "var(--or)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
            />
          </div>

          {/* Folder */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", display: "block", marginBottom: 5 }}>
              Destination folder
            </label>
            <select
              value={item.folder}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => onFolderChange(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer", appearance: "none" }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "var(--or)"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
            >
              {FOLDERS.map((f) => <option key={f} value={f} style={{ background: "#1a1917" }}>{f}</option>)}
            </select>
          </div>

          {/* Alt text (images only) */}
          {isImage && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", display: "block", marginBottom: 5 }}>
                Alt text <span style={{ color: "var(--text3)", fontWeight: 400 }}>(images only)</span>
              </label>
              <input
                value={item.altText}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onAltChange(e.target.value)}
                placeholder="Describe the image for accessibility…"
                style={inputStyle}
                onFocus={(e) => { e.currentTarget.style.borderColor = "var(--or)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Upload tips panel ────────────────────────────────────────────────────────

export function UploadTips() {
  const tips = [
    { icon: "📐", title: "Image dimensions", body: "Hero images: 1440×600 px. Product shots: 800×800 px min. Always use JPG or WebP for photos." },
    { icon: "📄", title: "Document formats", body: "Use PDF for certificates and published reports. DOCX for editable templates." },
    { icon: "🗜", title: "Archive files", body: "Compress multiple assets into a ZIP before uploading for batch delivery." },
    { icon: "⚖️", title: "File size limits", body: "Max 100 MB per file. Videos over 50 MB may take a few minutes to process." },
  ];
  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--section-header)" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Upload guidelines</div>
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {tips.map((t) => (
          <div key={t.title} style={{ display: "flex", gap: 10 }}>
            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{t.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 2 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.6 }}>{t.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Accepted types panel ─────────────────────────────────────────────────────

export function AcceptedTypes() {
  const rows = [
    { type: "image" as FileType, exts: ["JPG", "PNG", "WebP", "SVG", "GIF"] },
    { type: "document" as FileType, exts: ["PDF", "DOCX", "DOC"] },
    { type: "spreadsheet" as FileType, exts: ["XLSX", "XLS"] },
    { type: "video" as FileType, exts: ["MP4", "MOV", "WebM"] },
    { type: "archive" as FileType, exts: ["ZIP", "TAR", "GZ"] },
  ];
  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--section-header)" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Accepted formats</div>
      </div>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((r) => {
          const cfg = TYPE_CONFIG[r.type];
          return (
            <div key={r.type} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 6, background: cfg.bg, border: `1px solid ${cfg.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0,
              }}>{cfg.icon}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: cfg.color, marginBottom: 2 }}>{cfg.label}</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {r.exts.map((ext) => (
                    <span key={ext} style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
                      background: cfg.bg, border: `1px solid ${cfg.border}`,
                      color: cfg.color, borderRadius: 3, padding: "1px 5px",
                    }}>{ext}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: 11, color: "var(--text3)", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 2 }}>
          Max 100 MB per file · Max {MAX_FILES} files per batch
        </div>
      </div>
    </div>
  );
}


// export function QueueItem({
//   item, expanded, onToggleExpand, onRemove, onFolderChange, onAltChange, onNameChange,
// }: {
//   item: QueuedFile;
//   expanded: boolean;
//   onToggleExpand: () => void;
//   onRemove: () => void;
//   onFolderChange: (f: string) => void;
//   onAltChange: (v: string) => void;
//   onNameChange: (v: string) => void;
// }) {
//   const [hov, setHov] = useState(false);
//   const isImage = item.type === "image";

//   const inputStyle: React.CSSProperties = {
//     width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)",
//     borderRadius: 7, padding: "7px 10px", fontSize: 13, color: "var(--text)",
//     outline: "none", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box",
//     transition: "border-color 0.15s",
//   };

//   return (
//     <div
//       onMouseEnter={() => setHov(true)}
//       onMouseLeave={() => setHov(false)}
//       style={{
//         background: item.status === "done" ? "rgba(109,220,109,0.04)"
//           : item.status === "error" ? "rgba(224,82,82,0.04)"
//             : hov ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.015)",
//         border: `1px solid ${item.status === "done" ? "rgba(109,220,109,0.15)"
//             : item.status === "error" ? "rgba(224,82,82,0.15)"
//               : hov ? "rgba(255,255,255,0.10)" : "var(--border)"
//           }`,
//         borderRadius: 12, overflow: "hidden",
//         transition: "all 0.15s",
//       }}
//     >
//       {/* Row header */}
//       <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
//         <FileThumbnail file={item} size={44} />

//         <div style={{ flex: 1, minWidth: 0 }}>
//           <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
//             {item.overrideName || item.file.name}
//           </div>
//           <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
//             <TypeBadge type={item.type} />
//             <span style={{ fontSize: 12, color: "var(--text3)" }}>{fmtSize(item.file.size)}</span>
//             <span style={{ fontSize: 12, color: "var(--text3)" }}>{item.folder}</span>
//           </div>
//           {(item.status === "uploading" || item.status === "done") && (
//             <ProgressBar progress={item.progress} status={item.status} />
//           )}
//           {item.status === "error" && item.error && (
//             <div style={{ fontSize: 12, color: "#e05252", marginTop: 4 }}>{item.error}</div>
//           )}
//         </div>

//         <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
//           <StatusChip status={item.status} progress={item.progress} />

//           {/* Expand toggle */}
//           {item.status !== "uploading" && item.status !== "done" && (
//             <button
//               onClick={onToggleExpand}
//               title={expanded ? "Collapse" : "Edit metadata"}
//               style={{
//                 width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
//                 background: expanded ? "var(--or-dim)" : "transparent",
//                 color: expanded ? "var(--or)" : "var(--text3)", cursor: "pointer",
//                 display: "flex", alignItems: "center", justifyContent: "center",
//                 fontSize: 13, transition: "all 0.15s",
//               }}
//             >
//               <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
//                 style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
//                 <polyline points="6 9 12 15 18 9" />
//               </svg>
//             </button>
//           )}

//           {/* Remove */}
//           {item.status !== "uploading" && (
//             <button
//               onClick={onRemove} title="Remove from queue"
//               style={{
//                 width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
//                 background: "transparent", color: "var(--text3)", cursor: "pointer",
//                 display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
//                 transition: "all 0.15s",
//               }}
//               onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(224,82,82,0.10)"; e.currentTarget.style.color = "#e05252"; }}
//               onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text3)"; }}
//             >×</button>
//           )}
//         </div>
//       </div>

//       {/* Expandable metadata */}
//       {expanded && item.status !== "done" && (
//         <div style={{
//           padding: "12px 14px 14px", borderTop: "1px solid var(--border)",
//           background: "rgba(255,255,255,0.01)",
//           display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
//         }}>
//           {/* Override name */}
//           <div>
//             <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", display: "block", marginBottom: 5 }}>
//               Override filename
//             </label>
//             <input
//               value={item.overrideName}
//               onChange={(e: ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
//               placeholder={item.file.name}
//               style={inputStyle}
//               onFocus={(e) => { e.currentTarget.style.borderColor = "var(--or)"; }}
//               onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
//             />
//           </div>

//           {/* Folder */}
//           <div>
//             <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", display: "block", marginBottom: 5 }}>
//               Destination folder
//             </label>
//             <select
//               value={item.folder}
//               onChange={(e: ChangeEvent<HTMLSelectElement>) => onFolderChange(e.target.value)}
//               style={{ ...inputStyle, cursor: "pointer", appearance: "none" }}
//               onFocus={(e) => { e.currentTarget.style.borderColor = "var(--or)"; }}
//               onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
//             >
//               {FOLDERS.map((f) => <option key={f} value={f} style={{ background: "#1a1917" }}>{f}</option>)}
//             </select>
//           </div>

//           {/* Alt text (images only) */}
//           {isImage && (
//             <div style={{ gridColumn: "1 / -1" }}>
//               <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", display: "block", marginBottom: 5 }}>
//                 Alt text <span style={{ color: "var(--text3)", fontWeight: 400 }}>(images only)</span>
//               </label>
//               <input
//                 value={item.altText}
//                 onChange={(e: ChangeEvent<HTMLInputElement>) => onAltChange(e.target.value)}
//                 placeholder="Describe the image for accessibility…"
//                 style={inputStyle}
//                 onFocus={(e) => { e.currentTarget.style.borderColor = "var(--or)"; }}
//                 onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
//               />
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// }

// // ─── Upload tips panel ────────────────────────────────────────────────────────

// export function UploadTips() {
//   const tips = [
//     { icon: "📐", title: "Image dimensions", body: "Hero images: 1440×600 px. Product shots: 800×800 px min. Always use JPG or WebP for photos." },
//     { icon: "📄", title: "Document formats", body: "Use PDF for certificates and published reports. DOCX for editable templates." },
//     { icon: "🗜", title: "Archive files", body: "Compress multiple assets into a ZIP before uploading for batch delivery." },
//     { icon: "⚖️", title: "File size limits", body: "Max 100 MB per file. Videos over 50 MB may take a few minutes to process." },
//   ];
//   return (
//     <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
//       <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--section-header)" }}>
//         <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Upload guidelines</div>
//       </div>
//       <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
//         {tips.map((t) => (
//           <div key={t.title} style={{ display: "flex", gap: 10 }}>
//             <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{t.icon}</span>
//             <div>
//               <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 2 }}>{t.title}</div>
//               <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.6 }}>{t.body}</div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }

// // ─── Accepted types panel ─────────────────────────────────────────────────────

// export function AcceptedTypes() {
//   const rows = [
//     { type: "image" as FileType, exts: ["JPG", "PNG", "WebP", "SVG", "GIF"] },
//     { type: "document" as FileType, exts: ["PDF", "DOCX", "DOC"] },
//     { type: "spreadsheet" as FileType, exts: ["XLSX", "XLS"] },
//     { type: "video" as FileType, exts: ["MP4", "MOV", "WebM"] },
//     { type: "archive" as FileType, exts: ["ZIP", "TAR", "GZ"] },
//   ];
//   return (
//     <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
//       <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", background: "var(--section-header)" }}>
//         <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Accepted formats</div>
//       </div>
//       <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
//         {rows.map((r) => {
//           const cfg = TYPE_CONFIG[r.type];
//           return (
//             <div key={r.type} style={{ display: "flex", alignItems: "center", gap: 10 }}>
//               <div style={{
//                 width: 28, height: 28, borderRadius: 6, background: cfg.bg, border: `1px solid ${cfg.border}`,
//                 display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0,
//               }}>{cfg.icon}</div>
//               <div>
//                 <div style={{ fontSize: 12, fontWeight: 600, color: cfg.color, marginBottom: 2 }}>{cfg.label}</div>
//                 <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
//                   {r.exts.map((ext) => (
//                     <span key={ext} style={{
//                       fontSize: 10, fontWeight: 700, letterSpacing: "0.5px",
//                       background: cfg.bg, border: `1px solid ${cfg.border}`,
//                       color: cfg.color, borderRadius: 3, padding: "1px 5px",
//                     }}>{ext}</span>
//                   ))}
//                 </div>
//               </div>
//             </div>
//           );
//         })}
//         <div style={{ fontSize: 11, color: "var(--text3)", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 2 }}>
//           Max 100 MB per file · Max {MAX_FILES} files per batch
//         </div>
//       </div>
//     </div>
//   );
// }
